/**
 * ANIMATED GIF AS THE HERO — Bynder only (Jack, 2026-09-16).
 *
 * The Main Image picker takes any web-size Bynder asset. A GIF is different in three
 * ways that the photo path would silently get wrong:
 *
 *  1. Bynder's transform (`?io=transform:fill,…`) renders ONE frame — a crop, a zoom or
 *     even the plain "fill to slot" flattens the animation. So a GIF pick stores the
 *     transform BASE with no `io=` at all, and the crop/zoom card is not offered.
 *  2. With no transform there is no resize: the block's `width`/`height` attributes frame
 *     the file as-is, so a GIF whose shape disagrees with the slot is stretched. The card
 *     says so when the asset's own pixels are known.
 *  3. Outlook on Windows (Word engine) shows only the first frame; Apple, Gmail and the
 *     web clients play it. The first frame has to read on its own.
 *
 * Whether Bynder actually serves the animated file from the base URL is not assumed — the
 * card fetches the bytes once through the backend's `/bynder-img` proxy and counts the
 * GIF frames (`gifFrameCount`); one frame means Bynder served a still, and the card says
 * that too, before the requester ships it.
 */

export interface GifAssetLike { ext?: string; name?: string }

/** the picker's asset is a GIF — by Bynder's extension field, else by the file name */
export function isGifAsset(it: GifAssetLike | null | undefined): boolean {
  if (!it) return false;
  if (/^gif$/i.test(String(it.ext || "").trim())) return true;
  return /\.gif$/i.test(String(it.name || "").trim());
}

/**
 * Count the image frames in a GIF byte stream by walking the block structure
 * (GIF89a §§ 15–26): each Image Descriptor (0x2C) is one frame; extensions (0x21) and
 * their sub-blocks are skipped; 0x3B is the trailer. Returns 0 for anything that is not
 * a GIF. Tolerant of a truncated stream — counts what is there.
 */
export function gifFrameCount(bytes: Uint8Array): number {
  if (!bytes || bytes.length < 13) return 0;
  const sig = String.fromCharCode(bytes[0], bytes[1], bytes[2]);
  if (sig !== "GIF") return 0;
  let p = 13;                                   /* header (6) + logical screen descriptor (7) */
  const packed = bytes[10];
  if (packed & 0x80) p += 3 * (1 << ((packed & 0x07) + 1));   /* the global colour table */
  let frames = 0;
  const skipSubBlocks = () => { while (p < bytes.length) { const n = bytes[p++]; if (n === 0) return; p += n; } };
  while (p < bytes.length) {
    const b = bytes[p++];
    if (b === 0x3B) break;                      /* trailer */
    if (b === 0x21) { p++; skipSubBlocks(); continue; }   /* extension: label byte, then sub-blocks */
    if (b === 0x2C) {                           /* image descriptor */
      frames++;
      if (p + 9 > bytes.length) break;
      const ipacked = bytes[p + 8];
      p += 9;
      if (ipacked & 0x80) p += 3 * (1 << ((ipacked & 0x07) + 1));  /* the local colour table */
      p++;                                      /* LZW minimum code size */
      skipSubBlocks();
      continue;
    }
    break;                                      /* unknown block — stop rather than mis-count */
  }
  return frames;
}

/** the note under a GIF pick — one line, no rules the requester cannot act on */
export function gifNote(frames: number | null, nat?: { w: number; h: number } | null, slot?: { w: number; h: number } | null): string {
  const parts: string[] = [];
  if (frames === 0 || frames === 1) parts.push("Bynder served a still image — this GIF will not animate. Ask for the original to be published.");
  else if (frames && frames > 1) parts.push("Animated GIF · " + frames + " frames");
  else parts.push("Animated GIF");
  if (nat && slot && nat.w && nat.h && (Math.abs(nat.w / nat.h - slot.w / slot.h) / (slot.w / slot.h) >= 0.03)) {
    parts.push("it is " + nat.w + "×" + nat.h + " and the slot is " + slot.w + "×" + slot.h + " — it will be stretched (no crop on a GIF)");
  }
  parts.push("Outlook on Windows shows the first frame only");
  return parts.join(" · ");
}
